<template lang="pug">
.validation-with-auth
  //- PASO 1: Ingresar código AUTH
  .step(v-if="step === 'code'")
    .step-title Ejecutar prestación con autorización
    .step-desc Ingresá el código de autorización que te presentó el afiliado
    .form-group
      label CÓDIGO DE AUTORIZACIÓN
      el-input(
        v-model="authCode"
        placeholder="AUTH-2026-XXXXXXXXXX"
        size="large"
        @keyup.enter.native="lookupAuth"
        :disabled="isLoading"
      )
    .form-error(v-if="lookupError") {{ lookupError }}
    el-button(
      type="primary"
      size="large"
      :loading="isLoading"
      :disabled="!authCode"
      @click="lookupAuth"
    ) Consultar autorización

  //- PASO 2: Preview de la autorización
  .step(v-if="step === 'preview'")
    .step-title Autorización encontrada
    .auth-card
      .auth-row
        span.auth-label Caso
        span.auth-val \#{{ authData.caseNumber }}
      .auth-row
        span.auth-label Código
        span.auth-val.mono {{ authData.authorizationCode }}
      .auth-row
        span.auth-label Afiliado
        span.auth-val {{ authData.member.lastName }}, {{ authData.member.firstName }}
      .auth-row
        span.auth-label Prestación
        span.auth-val
          span.practice-code {{ authData.practice.code }}
          span {{ authData.practice.name }}
      .auth-row(v-if="authData.approvedUntil")
        span.auth-label Vigente hasta
        span.auth-val {{ authData.approvedUntil | formatDate }}
    .contract-warning(v-if="authData.contractWarning")
      i.el-icon-warning
      span {{ authData.contractWarning }}
    .contract-ok(v-if="authData.canExecute")
      i.el-icon-circle-check
      span Prestación disponible en tu contrato
    .form-group(v-if="authData.canExecute" style="margin-top: 16px")
      label TOKEN OTP DEL AFILIADO
      el-input(
        v-model="memberToken"
        placeholder="000"
        size="large"
        maxlength="6"
        :disabled="isLoading"
        @keyup.enter.native="execute"
      )
    .form-error(v-if="executeError") {{ executeError }}
    .step-actions
      el-button(size="large" @click="reset") Volver
      el-button(
        v-if="authData.canExecute"
        type="primary"
        size="large"
        :loading="isLoading"
        :disabled="!memberToken"
        @click="execute"
      ) Ejecutar prestación

  //- PASO 3: Éxito
  .step.step-success(v-if="step === 'success'")
    i.el-icon-circle-check.success-icon
    h3 Prestación ejecutada
    .trx-code {{ lastTrx }}
    p Guardá este código como comprobante
    el-button(type="primary" @click="reset") Nueva validación
</template>

<script>
import authorizationService from "@/services/authorization";
import authorizationExecuteService from "@/services/authorization-execute";

/**
 * @component ValidationWithAuth
 * @description Flujo de dos pasos para ejecutar una prestación con código AUTH.
 * Paso 1: consulta la autorización y verifica contrato del prestador.
 * Paso 2: ingresa OTP y ejecuta.
 */
export default {
  name: "ValidationWithAuth",
  filters: {
    /**
     * @filter formatDate
     * @description Formatea fecha ISO a dd/mm/yyyy
     */
    formatDate(val) {
      if (!val) return "—";
      return new Date(val).toLocaleDateString("es-AR");
    },
  },
  data() {
    return {
      step: "code",
      authCode: "",
      memberToken: "",
      authData: null,
      isLoading: false,
      lookupError: null,
      executeError: null,
      lastTrx: null,
    };
  },
  methods: {
    /**
     * @method lookupAuth
     * @description Consulta la autorización por código AUTH sin ejecutarla
     */
    async lookupAuth() {
      if (!this.authCode) return;
      this.isLoading = true;
      this.lookupError = null;
      try {
        const data = await authorizationService.lookup(this.authCode);
        this.authData = data;
        this.step = "preview";
      } catch (e) {
        this.lookupError =
          e.response?.data?.error || "Error al consultar la autorización";
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * @method execute
     * @description Ejecuta la prestación con OTP del afiliado
     */
    async execute() {
      if (!this.memberToken) return;
      this.isLoading = true;
      this.executeError = null;
      try {
        const data = await authorizationExecuteService.execute(
          this.authCode,
          this.memberToken
        );
        this.lastTrx = data.transactionCode;
        this.step = "success";
      } catch (e) {
        this.executeError =
          e.response?.data?.error || "Error al ejecutar la prestación";
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * @method reset
     * @description Resetea el formulario al estado inicial
     */
    reset() {
      this.step = "code";
      this.authCode = "";
      this.memberToken = "";
      this.authData = null;
      this.lookupError = null;
      this.executeError = null;
      this.lastTrx = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.validation-with-auth {
  padding: 24px 0 8px;
}

.step {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 480px;
}

.step-title {
  font-size: 16px;
  font-weight: 600;
  color: #002d5e;
}

.step-desc {
  font-size: 13px;
  color: #6b7294;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  label {
    font-size: 11px;
    font-weight: 600;
    color: #6b7294;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.form-error {
  font-size: 13px;
  color: #a32d2d;
  background: #fcebeb;
  padding: 10px 14px;
  border-radius: 8px;
}

.auth-card {
  background: #f8f9fc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.auth-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 13px;
}

.auth-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7294;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.auth-val {
  color: #1a1f36;
  font-weight: 500;
  text-align: right;
  display: flex;
  gap: 6px;
  align-items: center;
}

.mono {
  font-family: monospace;
  color: #05c49a;
  font-weight: 700;
}

.practice-code {
  font-size: 11px;
  font-weight: 700;
  color: #002d5e;
  background: #eef3ff;
  padding: 2px 7px;
  border-radius: 4px;
}

.contract-warning {
  background: #fef3cd;
  border: 1px solid #f5c518;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #854f0b;
  display: flex;
  align-items: center;
  gap: 8px;
  i {
    font-size: 16px;
  }
}

.contract-ok {
  background: #eaf3de;
  border: 1px solid #3b6d11;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #3b6d11;
  display: flex;
  align-items: center;
  gap: 8px;
  i {
    font-size: 16px;
  }
}

.step-actions {
  display: flex;
  gap: 10px;
}

.step-success {
  align-items: center;
  text-align: center;
  padding: 32px 0;
}

.success-icon {
  font-size: 48px;
  color: #1d9e75;
}

.trx-code {
  font-family: monospace;
  font-size: 22px;
  font-weight: 700;
  color: #002d5e;
  background: #eef3ff;
  padding: 10px 24px;
  border-radius: 8px;
}

// Botones Element UI redondeados (8px) — homogeneiza con el resto del
// producto, que usa botones con borde redondeado.
::v-deep .el-button {
  border-radius: 8px;
}
</style>
